import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';

/**
 * Post-download data-capture prompt for users who created their account through
 * a download attempt (DEV-6521).
 *
 * The existing data-capture questionnaire (`onboarding.questionnaire`) fires
 * straight after a standalone signup, but the download journeys deliberately
 * skip it so the download the user came for isn't interrupted. This machine
 * defers it instead: the download-journey signup marks the new account here,
 * their first completed download promotes it to `pending`, and a short delay
 * later the questionnaire is presented ({@link usePostDownloadDataCapture}).
 *
 * States:
 * - `idle`              — nothing owed. The default, and the terminal state
 *                         once the prompt has been shown.
 * - `awaiting-download` — account just created via a download attempt (free or
 *                         premium); waiting for the first download to complete.
 * - `pending`           — first download done; the prompt is owed and will be
 *                         presented this session or the next.
 *
 * Persisted in `localStorage` so a `pending` prompt survives the user closing
 * the site before the delay elapses — it is then shown in their next session
 * (the ticket's secondary trigger).
 */
export type PostDownloadDataCaptureState =
  | 'idle'
  | 'awaiting-download'
  | 'pending';

export const POST_DOWNLOAD_DATA_CAPTURE_KEY = 'ub:post-download-data-capture';

export const postDownloadDataCaptureAtom =
  atomWithStorage<PostDownloadDataCaptureState>(
    POST_DOWNLOAD_DATA_CAPTURE_KEY,
    'idle'
  );

/**
 * How long a user browses after their first download before the prompt appears
 * — "30 seconds in that section" per the ticket. Re-used unchanged on the
 * second session so the user gets the same beat of browsing before it opens.
 */
export const POST_DOWNLOAD_DATA_CAPTURE_DELAY_MS = 30_000;

/**
 * Mark that the current account was created via a download attempt. Called from
 * the download journey's signup success handler (both free and premium). Only
 * advances an `idle` machine so a `pending` prompt already owed from an earlier
 * download isn't reset.
 */
export const markSignedUpViaDownloadAtom = atom(null, (get, set) => {
  if (get(postDownloadDataCaptureAtom) === 'idle') {
    set(postDownloadDataCaptureAtom, 'awaiting-download');
  }
});

/**
 * Promote to `pending` once a download completes, but only for an account that
 * was created via a download attempt (`awaiting-download`). Returning users who
 * logged in to download never entered that state, so they're never prompted.
 */
export const markDownloadCompletedAtom = atom(null, (get, set) => {
  if (get(postDownloadDataCaptureAtom) === 'awaiting-download') {
    set(postDownloadDataCaptureAtom, 'pending');
  }
});
